import React, { useRef, useState, useCallback } from "react";
import { ErrorMessage } from "formik";
import CloudStorage from "../../../../CloudStorage/CloudStorage";
import { urtoast } from "../../../Layouts/CustomPopup/eventEmitter";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../../../utils/cropUtils";
import { validateFileSignature } from "../../../../utils/fileValidation";
import AsteriskLabel from "../../../Common/AsteriskLabel";
import { useFileValidator } from "../../../../hooks/useFileValidator";
import UploadLoader from "../../../Common/Loader/UploadLoader";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const ProfileImageUpload = ({
  selectedImage,
  setSelectedImage,
  setFieldValue,
  initialImage = null,
}) => {
  const fileInputRef = useRef(null);
  const { validating, validateFiles } = useFileValidator();

  // Cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [tempImage, setTempImage] = useState(null);

  const handleImageChange = async (event) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    // 🛡️ Validate all files via backend API
    const isValid = await validateFiles([file]);
    if (!isValid) return;

    // Check file size (10 MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      urtoast("File size should not exceed 10 MB", 2);
      return;
    }

    const imageURL = URL.createObjectURL(file);
    setTempImage(imageURL);
    setShowCropper(true);
    setFieldValue("image", file); // still set original file initially
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedFile = await getCroppedImg(tempImage, croppedAreaPixels);
      setFieldValue("image", croppedFile); // set cropped file in Formik
      setSelectedImage(URL.createObjectURL(croppedFile));
      setShowCropper(false);
    } catch (err) {
      console.error(err);
      urtoast("Failed to crop image", 2);
    }
  };

  const handleUploadPhotoClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="signup-form position-relative">
      {validating && <UploadLoader />}
      <div className="signup-inner">
        <div className="signup-img" onClick={handleUploadPhotoClick}>
          <button className="edit-btn" type="button">
            <img
              className="w-100"
              src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/edit.svg`}
              alt="Edit"
            />
          </button>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
          {selectedImage && !showCropper ? (
            <img
              src={selectedImage}
              alt="Selected"
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "contain",
              }}
            />
          ) : !selectedImage ? (
            <img
              src={`${CloudStorage.cloudurl}/Assets/Images/common/user-icon.svg`}
              alt="Default"
              style={{ width: "84px", height: "84px" }}
            />
          ) : null}
        </div>

        {!selectedImage && (
          <p
            style={{
              color: "#EF7710",
              fontSize: "16px",
              cursor: "pointer",
              alignSelf: "center",
            }}
            onClick={handleUploadPhotoClick}
          >
            Upload photo
          </p>
        )}

        <ErrorMessage name="image" component="div" className="error" />

        {/* Cropper Modal */}
        {showCropper && tempImage && (
          <div
            style={{
              position: "relative",
              width: 300,
              height: 300,
              margin: "20px auto",
              background: "#333",
            }}
          >
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
            <button
              type="button"
              onClick={handleCropSave}
              style={{
                position: "absolute",
                bottom: 10,
                left: "50%",
                transform: "translateX(-50%)",
                padding: "5px 15px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              Crop & Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileImageUpload;
